/**
 * The `free` pricing model, and the one rule that makes it safe: a listing whose
 * model is `free` costs 0, and no price field on the doc is consulted to decide
 * that.
 *
 * WHY THIS EXISTS AS A SHARED MODULE. `free` was added after the money model had
 * settled and was never reconciled with it, so every price reader treated it as
 * an ordinary standard-priced product:
 *
 *   - both product forms wrote the seller's leftover `standardPrice` into
 *     `price` even when the model was `free`, so switching the model to Free
 *     after typing a price left the amount parked on the doc;
 *   - the cart added the line at `discountedPrice || price`;
 *   - the server's resolveItemPriceAud had no `free` branch and fell through to
 *     the standard one, which charges that same leftover amount;
 *   - meanwhile the product page rendered A$0.00 for the model unconditionally.
 *
 * The buyer was shown Free and charged. As Merchant of Record that is supa.place's
 * chargeback and supa.place's misleading-price problem, not the seller's, which is
 * why the read side and the write side are both pinned here rather than fixed at
 * one call site.
 *
 * The BINDING check is resolveItemPriceAud in functions/src/index.ts: it returns 0
 * for this model no matter what the doc carries, which is what protects the
 * products already sitting in Firestore with a stale price. Everything here keeps
 * the client honest and stops new docs being written that way. Keep the two in
 * step.
 */

export type FreePricingInput = {
  pricingModel?: string | null;
  price?: number | null;
  discountedPrice?: number | null;
};

export function isFreePricingModel(pricingModel?: string | null): boolean {
  return pricingModel === 'free';
}

/**
 * What a cart line for this product costs, in AUD.
 *
 * Free short-circuits before any price field is read. Every other model keeps the
 * previous behaviour exactly: the discounted price when there is one, otherwise
 * the list price. `Number(undefined)` is NaN and NaN is falsy, so an absent field
 * falls through the chain the same way the original `discountedPrice || price`
 * expression did, with a final 0 so a malformed doc can never put NaN in the cart.
 *
 * Tiered, PWYW and subscription lines are NOT priced here: they carry a tier id or
 * a buyer-named amount and are built at their own call sites.
 */
export function cartPriceAud(product: FreePricingInput): number {
  if (isFreePricingModel(product.pricingModel)) return 0;
  return Number(product.discountedPrice) || Number(product.price) || 0;
}

/**
 * Normalise a product payload before it is written, so a free listing never
 * carries a chargeable amount in the first place.
 *
 * `discountedPrice` is set to null rather than deleted: firestore.rules accepts a
 * null there (see priceFieldsWithinCap) and a seller who switches an actively
 * discounted product to Free must not leave the old discount behind. Mutates and
 * returns the object it is given, which is how both forms already build their
 * write payloads.
 */
export function applyFreePricing<T extends Record<string, any>>(data: T): T {
  if (!isFreePricingModel(data.pricingModel)) return data;
  const target = data as Record<string, unknown>;
  target.price = 0;
  target.standardPrice = 0;
  target.discountedPrice = null;
  return data;
}
